/** Upload and verify server-provided side/format targets with independent retries. */
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Loader2, RefreshCw, UploadCloud } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  usePrintFileIssueBulkUpload,
  type BulkUploadRow,
  type RowStatus,
  type SelfHealKind,
} from '../hooks/usePrintFileIssueBulkUpload';
import { PrintFileTargetRow } from './PrintFileTargetRow';
import type { PrintFileTargetPhase } from '../printFileTargetState';

/** Minimal per-issue display info the modal needs. */
export interface BulkUploadIssueSummary {
  issueId: string;
  sku: string | null;
  orderName: string | null;
  productName: string;
  /** Default print-file kind for this issue (derived by the caller). */
  defaultKind: SelfHealKind;
}

interface PrintFileIssueBulkUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issues: BulkUploadIssueSummary[];
  /** Called after the fan-out completes so the list can refetch. */
  onComplete?: () => void;
}

/** The bulk row's status maps 1:1 onto the shared row's phase; 'queued' is
 *  just this surface's name for idle. */
function rowPhase(status: RowStatus): PrintFileTargetPhase {
  return status === 'queued' ? 'idle' : status;
}

export function PrintFileIssueBulkUploadModal({
  open,
  onOpenChange,
  issues,
  onComplete,
}: PrintFileIssueBulkUploadModalProps) {
  const { t } = useTranslation('production');
  const bulk = usePrintFileIssueBulkUpload();
  const {
    initRows,
    reset,
    run,
    rows,
    isUploading,
    succeeded,
    failed,
    total,
    stagedCount,
    anyRowHasFile,
  } = bulk;

  // Seed rows whenever the modal opens with a fresh selection.
  useEffect(() => {
    if (open) {
      initRows(issues.map((i) => ({ issueId: i.issueId, kind: i.defaultKind })));
    } else {
      reset();
    }
    // issues identity is stable per open; intentionally keyed on `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // SETTLED, not "all succeeded". A run where 3 of 5 files failed is finished:
  // the operator is owed the partial toast, the Done button and the list
  // refresh, not a modal frozen mid-run with a live Cancel.
  const allSettled = total > 0 && succeeded + failed === total;
  const completedRun = allSettled && !isUploading;
  // Anything actually happened — a partial run (files staged for only some
  // rows) still has to refresh the list underneath on close.
  const ranSomething = succeeded > 0 || failed > 0;

  // Group by issue so a dual product's two sides read as one item's work
  // rather than two unrelated rows.
  const groups = useMemo(() => {
    const byIssue = new Map<string, Array<[string, BulkUploadRow]>>();
    for (const entry of Object.entries(rows)) {
      const list = byIssue.get(entry[1].issueId) ?? [];
      list.push(entry);
      byIssue.set(entry[1].issueId, list);
    }
    return [...byIssue.entries()];
  }, [rows]);

  const summary = useMemo(() => {
    if (isUploading || ranSomething) {
      return t('issues.printFiles.bulk.summaryProgress', {
        done: succeeded + failed,
        total,
      });
    }
    return t('issues.printFiles.bulk.summaryReady', { count: total });
  }, [isUploading, ranSomething, succeeded, failed, total, t]);

  const handleUpload = async () => {
    await run();
  };

  const handleClose = () => {
    // Any completed work (whole run or partial) triggers the list refetch.
    if (completedRun || ranSomething) onComplete?.();
    onOpenChange(false);
  };

  // Surface a single toast on completion — including the PARTIAL case, which
  // previously produced no toast at all because the run never counted as done.
  useEffect(() => {
    if (completedRun) {
      if (failed === 0) {
        toast.success(t('issues.printFiles.bulk.allHealed', { count: succeeded }));
      } else if (succeeded === 0) {
        toast.error(t('issues.printFiles.bulk.allFailed', { count: failed }));
      } else {
        toast.warning(t('issues.printFiles.bulk.partial', { succeeded, failed }));
      }
    }
    // Fire once per transition into the completed state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedRun]);

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-[640px]">
        <DialogHeader>
          {/* Counts the FILES to upload, not the issues — a dual product needs
              up to four, and "Upload 2 print files" for eight dropzones read
              as a bug. Falls back to the selection size until the matrices load. */}
          <DialogTitle>
            {t('issues.printFiles.bulk.title', { count: total || issues.length })}
          </DialogTitle>
          <DialogDescription>{t('issues.printFiles.bulk.description')}</DialogDescription>
        </DialogHeader>

        {/* Scrollable row list */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1 [overscroll-behavior:contain]">
          <div role="status" aria-live="polite" className="empty:hidden">
            {bulk.isLoading && (
              <p className="text-base">{t('issues.printFiles.upload.loading')}</p>
            )}
          </div>
          {Object.entries(bulk.errors).map(([issueId, error]) => (
            <p key={issueId} role="alert" className="text-base text-destructive">
              {issues.find((issue) => issue.issueId === issueId)?.productName}: {error}
            </p>
          ))}
          {groups.map(([issueId, entries]) => {
            const issue = issues.find((candidate) => candidate.issueId === issueId);
            const headingId = `bulk-issue-${issueId}`;
            return (
              <section
                key={issueId}
                aria-labelledby={headingId}
                className="space-y-2 rounded-lg border p-4"
              >
                <h3 id={headingId} className="text-lg font-semibold">
                  {issue?.productName}
                </h3>
                <p className="font-mono text-base text-muted-foreground">
                  {[issue?.sku, issue?.orderName].filter(Boolean).join(' · ')}
                </p>
                {entries.map(([key, row]) => (
                  <div key={key} className="space-y-1">
                    <p className="text-base font-medium">
                      {t(`issues.printFiles.printData.${row.side}Title`)}
                    </p>
                    <PrintFileTargetRow
                      target={{
                        side: row.side,
                        format: row.format,
                        filename: row.filename,
                        status: row.targetStatus,
                        locked: row.locked,
                        awaitingBridge: row.awaitingBridge,
                        downloadUrl: row.downloadUrl,
                      }}
                      sideLabel={t(`issues.printFiles.printData.${row.side}Title`)}
                      file={row.file}
                      phase={rowPhase(row.status)}
                      message={row.detail}
                      uploaded={row.uploaded}
                      disabled={isUploading}
                      compact
                      idSuffix={key}
                      testId={`bulk-target-${key}`}
                      onFileChange={(file) => bulk.setRowFile(key, file)}
                    />
                    <RowStatusBadge status={row.status} />
                  </div>
                ))}
              </section>
            );
          })}
        </div>

        {/* Sticky summary */}
        <div
          role="status"
          aria-live="polite"
          className="shrink-0 rounded-md bg-muted/60 px-3 py-2 text-sm text-muted-foreground"
        >
          {summary}
          {(succeeded > 0 || failed > 0) && (
            <span className="ml-2 inline-flex items-center gap-2">
              {succeeded > 0 && (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                  {succeeded}
                </span>
              )}
              {failed > 0 && (
                <span className="inline-flex items-center gap-1 text-red-700">
                  <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                  {failed}
                </span>
              )}
            </span>
          )}
        </div>

        {/* Secondary actions first, the primary (upload) last — the action
            that closes issues sits where the operator's eye lands. */}
        <DialogFooter>
          <Button
            className="h-12 gap-2 text-base"
            variant="outline"
            disabled={isUploading || bulk.isLoading}
            onClick={() =>
              void initRows(issues.map((issue) => ({ issueId: issue.issueId, kind: issue.defaultKind })))
            }
          >
            <RefreshCw
              className={`h-5 w-5 ${bulk.isLoading ? 'motion-safe:animate-spin' : ''}`}
              aria-hidden="true"
            />
            {t('issues.printFiles.upload.refreshStatus')}
          </Button>
          <Button
            className="h-12 text-base"
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            {completedRun ? t('issues.printFiles.bulk.done') : t('issues.resolve.cancelBtn')}
          </Button>
          {!completedRun && (
            <Button
              onClick={handleUpload}
              disabled={!anyRowHasFile || isUploading}
              className="h-12 gap-1.5 bg-threadheads-purple text-base text-white hover:bg-threadheads-purple/90"
            >
              {isUploading ? (
                <Loader2 className="h-5 w-5 motion-safe:animate-spin" aria-hidden="true" />
              ) : (
                <UploadCloud className="h-5 w-5" aria-hidden="true" />
              )}
              {t('issues.printFiles.bulk.upload', { count: stagedCount })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function RowStatusBadge({ status }: { status: RowStatus }) {
  const { t } = useTranslation('production');
  if (status === 'success') {
    return (
      <span className="motion-safe:animate-check-enter inline-flex shrink-0 items-center gap-1 text-base font-medium text-emerald-700">
        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        {t('issues.printFiles.bulk.statusDone')}
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-base font-medium text-red-700">
        <XCircle className="h-4 w-4" aria-hidden="true" />
        {t('issues.printFiles.bulk.statusFailed')}
      </span>
    );
  }
  return null;
}
